import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import MobileLayout from '@/components/MobileLayout';
import FitnessDataState from '@/components/fitness/FitnessDataState';
import WorkoutNumberInput from '@/components/fitness/WorkoutNumberInput';
import { useFitnessResource } from '@/hooks/useFitnessResource';
import { useLanguage } from '@/i18n/LanguageProvider';
import { supabase } from '@/integrations/supabase/client';
import { buildWorkoutSuggestion } from '@/lib/fitness/workoutSuggestion';
import { freeWorkoutRouteItems } from '@/lib/fitness/freeWorkoutBuilder';

const goals = [
  ['general', 'fitness.onboarding.goalGeneral'], ['muscle', 'fitness.onboarding.goalMuscle'],
  ['lose', 'fitness.onboarding.goalLose'], ['endurance', 'fitness.onboarding.goalEndurance'],
] as const;
const gear = [
  ['bodyweight', 'fitness.onboarding.equipBodyweight'], ['dumbbells', 'fitness.onboarding.equipDumbbells'],
  ['barbell', 'fitness.onboarding.equipBarbell'], ['cables', 'fitness.onboarding.equipCables'],
  ['full_gym', 'fitness.onboarding.equipFullgym'], ['home_gym', 'fitness.onboarding.equipHomegym'],
] as const;
async function readProfile(userId: string, assertCurrent: () => void) {
  const result = await supabase.from('fitness_profiles').select('goal,level,equipment').eq('user_id', userId).maybeSingle();
  assertCurrent(); if (result.error) throw result.error; return result.data;
}
export default function WorkoutSuggestion() {
  const { t, tf } = useLanguage(), navigate = useNavigate();
  const resource = useFitnessResource(readProfile), seededFor = useRef<string | null>(null);
  const [readyOwner, setReadyOwner] = useState<string | null>(null);
  const [goal, setGoal] = useState('general'), [level, setLevel] = useState('beginner');
  const [equipment, setEquipment] = useState('bodyweight'), [minutes, setMinutes] = useState(30), [valid, setValid] = useState(true);
  useEffect(() => {
    if (!resource.userId) { seededFor.current = null; setReadyOwner(null); return; }
    if (resource.status !== 'ready' || seededFor.current === resource.userId) return;
    seededFor.current = resource.userId;
    const profile = resource.data;
    setGoal(goals.some(([key]) => key === profile?.goal) ? profile!.goal : 'general');
    setLevel(['beginner', 'intermediate', 'advanced'].includes(profile?.level ?? '') ? profile!.level : 'beginner');
    const available = Array.isArray(profile?.equipment) ? profile.equipment : [];
    setEquipment(available.includes('full_gym') ? 'full_gym' : gear.find(([key]) => available.includes(key))?.[0] ?? 'bodyweight');
    setMinutes(30); setValid(true); setReadyOwner(resource.userId);
  }, [resource.userId, resource.status, resource.data]);
  const suggestion = useMemo(() => buildWorkoutSuggestion({ goal, level, equipment: [equipment], days_per_week: 2 }, minutes), [goal, level, equipment, minutes]);
  if (resource.status !== 'ready' || readyOwner !== resource.userId) return <MobileLayout className="pb-28 pt-8"><FitnessDataState status={resource.status === "ready" ? "loading" : resource.status} busy={resource.isRefreshing} onRetry={resource.refresh} /></MobileLayout>;
  const selectClass = 'mt-2 min-h-12 w-full rounded-xl border border-border bg-background px-3 py-2 text-foreground';
  return <MobileLayout className="pb-28 pt-8">
    <button type="button" onClick={() => navigate('/fitness/plans')} className="mb-5 flex min-h-11 items-center gap-1 text-sm text-muted-foreground"><ChevronLeft size={18} />{t('common.back')}</button>
    <p className="mb-2 text-xs uppercase tracking-[0.18em] text-primary">{t('fitness.builder.optional')}</p>
    <h1 className="font-display text-2xl font-semibold">{t('fitness.builder.suggestion')}</h1>
    <p className="mb-6 mt-2 text-sm text-muted-foreground">{t('fitness.builder.suggestionDescription')}</p>
    <div className="space-y-5">
      <label className="block text-sm">{t('fitness.onboarding.goal')}
        <select className={selectClass} value={goal} onChange={event => setGoal(event.target.value)}>
          {goals.map(([key, label]) => <option key={key} value={key}>{t(label)}</option>)}
        </select>
      </label>
      <label className="block text-sm">{t('fitness.onboarding.level')}
        <select className={selectClass} value={level} onChange={event => setLevel(event.target.value)}>
          {['beginner', 'intermediate', 'advanced'].map(key => <option key={key} value={key}>{t('fitness.level.' + key)}</option>)}
        </select>
      </label>
      <label className="block text-sm">{t('fitness.onboarding.equipment')}
        <select className={selectClass} value={equipment} onChange={event => setEquipment(event.target.value)}>
          {gear.map(([key, label]) => <option key={key} value={key}>{t(label)}</option>)}
        </select>
      </label>
      <WorkoutNumberInput label={t('fitness.builder.minutes')} value={minutes} min={10} max={120} integer onChange={setMinutes} onValidityChange={setValid} />
    </div>
    <p className="mt-6 text-sm text-muted-foreground">{t('fitness.builder.durationEstimate')}</p>
    <button type="button" disabled={!valid || !suggestion.length || !resource.userId}
      onClick={() => { if (!resource.userId || !resource.capture()() || !valid) return;
        navigate('/fitness/free-workout/review', { state: { ownerId: resource.userId, source: 'suggestion', exercises: freeWorkoutRouteItems(suggestion) } });
      }} className="mt-5 min-h-12 w-full rounded-xl bg-primary px-4 py-3 font-display font-semibold text-primary-foreground disabled:opacity-40">
      {tf('fitness.builder.reviewSuggestion', { count: String(suggestion.length) })}
    </button>
  </MobileLayout>;
}
